import PropTypes from 'prop-types';
import { useCallback } from 'react';
// @mui
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
// components
import { Box, Checkbox, FormControlLabel, Grid, IconButton, Typography } from '@mui/material';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import { USER_TYPE_SUPER_ADMIN } from 'src/config-global';
import EstimateCategoryTableFilter from 'src/sections/main-sections/estimator-settings/estimate-categories/estimate-category-table-filter';

// ----------------------------------------------------------------------

export default function UserTableToolbar({
  filters,
  onFilters,
  calenderBtn,
  filterBtn,
  sortBtn,
  sortBy,
  setSortBy,
  // When set (one of ESTIMATE_CATEGORY_TYPES) the toolbar also renders a category
  // multi-select that drives `filters.category` — used by the Primary Service,
  // Additional Service and Terms & Conditions listings.
  categoryFilterType,
}) {

  const popover = usePopover();

  const router=useRouter();

  const {user}=useMockedUser();

  const isAdmin=user?.user_type===USER_TYPE_SUPER_ADMIN;

  const handleFilterName = useCallback(
    (event) => {
      onFilters("name" , event.target.value);
    },
    [onFilters]
  );

  const handleCheckboxChange = (id) => {
    setSortBy((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: !item.status } : {...item, status: false}
      )
    );
  };

  const searchField = (
    <TextField
      fullWidth
      value={filters.name}
      onChange={handleFilterName}
      placeholder="Search..."
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <>
      <Stack
        spacing={2}
        alignItems={{ xs: 'flex-end', md: 'center' }}
        direction={{
          xs: 'column',
          md: 'row',
        }}
        sx={{
          p: 2.5,
          pr: { xs: 2.5, md: 1 },
        }}
      >
        {categoryFilterType ? (
          <Grid container spacing={2} alignItems="center" sx={{ width: 1 }}>
            <Grid item xs={12} md={3}>
              <EstimateCategoryTableFilter
                type={categoryFilterType}
                value={filters.category || []}
                onChange={(ids) => onFilters('category', ids)}
                size="medium"
              />
            </Grid>
            <Grid item xs={12} md={9}>
              {searchField}
            </Grid>
          </Grid>
        ) : (
          <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1, height: 1 }}>
            {searchField}
          </Stack>
        )}
        <Stack direction="row" spacing={1} >
          {calenderBtn && <IconButton variant='contained' size="large" onClick={()=>isAdmin ? router.push(paths.superAdminDashboard.calendar) : router.push(paths.dashboard.calendar)}><Iconify icon="solar:calendar-bold" /></IconButton>}
          {(sortBtn || filterBtn) && <IconButton onClick={popover.onOpen} sx={{ width: "40px", height: "40px" }}>
            <Iconify icon="mdi:filter-outline" />
          </IconButton>}
        </Stack>
        <CustomPopover
          open={popover.open}
          onClose={popover.onClose}
          arrow="right-center"
          sx={{ minWidth: 160 }}
        >
          {sortBy && <Box sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
            <Typography sx={{ mb: 1, fontWeight: '400' }}>{sortBtn && "Sort by" || filterBtn && "Filter by"}</Typography>
            {sortBy.map((option) => (
              <FormControlLabel
                key={option.id}
                control={<Checkbox checked={option.status} onChange={() => handleCheckboxChange(option.id)} />}
                label={option.label}
              />
            ))}
          </Box>}
        </CustomPopover>
      </Stack>

    </>
  );
}

UserTableToolbar.propTypes = {
  filters: PropTypes.object,
  onFilters: PropTypes.func,
  roleOptions: PropTypes.array,
  categoryFilterType: PropTypes.string,
};
